import { useEffect, useState, useMemo } from "react";
import {
  getAPInvoices, getAPInvoicePDF, updateAPInvoiceStatus, getAPSelectedInvoicesPDF,
  getAPSelectedInvoicesZip, getAPInvoiceBatches, getAPBatchInvoicePDF,
  getAPSettings, updateAPSettings, cancelAPInvoice, api
} from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import {
  Download, Printer, Search, RefreshCw, FileCheck, CheckCircle, Clock, XCircle,
  FileArchive, FileText, Calendar, Layers, ArrowDown, ArrowUp, Ban, Settings, Save
} from "lucide-react";
import { toast } from "sonner";

export default function APInvoiceHistory() {
  const [invoices, setInvoices] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [invoiceDates, setInvoiceDates] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => { loadInvoices(); loadBatches(); }, []);

  const loadBatches = async () => { try { const r = await getAPInvoiceBatches(); setBatches(r.data); } catch {} };

  useEffect(() => {
    const dates = [...new Set(invoices.map(inv => {
      const d = new Date(inv.created_at || inv.invoice_date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }))].sort((a, b) => new Date(b) - new Date(a));
    setInvoiceDates(dates);
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];
    if (dateFilter !== "all") {
      filtered = filtered.filter(inv => {
        const d = new Date(inv.created_at || inv.invoice_date);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) === dateFilter;
      });
    }
    if (searchTerm) {
      filtered = filtered.filter(inv =>
        (inv.invoice_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.po_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.buyer_store || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    filtered.sort((a, b) => {
      const dateA = new Date(a.invoice_date);
      const dateB = new Date(b.invoice_date);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    return filtered;
  }, [invoices, searchTerm, dateFilter, sortOrder]);

  const loadInvoices = async () => {
    try { const r = await getAPInvoices(); setInvoices(r.data || []); }
    catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  };

  const handleDownload = async (invoice) => {
    try {
      const r = await getAPInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${invoice.invoice_number}.pdf`; a.click();
    } catch { toast.error("Failed to download"); }
  };

  const handlePrint = async (invoice) => {
    try {
      const r = await getAPInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const pw = window.open(url); if (pw) pw.onload = () => pw.print();
      await updateAPInvoiceStatus(invoice.id, 'printed'); await loadInvoices();
    } catch { toast.error("Failed to print"); }
  };

  const handleCancelInvoice = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try { const r = await cancelAPInvoice(cancelTarget.id); toast.success(r.data.message); setCancelTarget(null); await loadInvoices(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setCancelling(false); }
  };

  const handleBatchPrint = async (batchId) => {
    setDownloading(true);
    try {
      const r = await getAPBatchInvoicePDF(batchId);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const pw = window.open(url); if (pw) pw.onload = () => pw.print();
    } catch { toast.error("Failed"); }
    finally { setDownloading(false); }
  };

  const handleBatchDownload = async (batchId) => {
    setDownloading(true);
    try {
      const r = await getAPBatchInvoicePDF(batchId);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `ap_batch_${batchId.slice(0, 8)}.pdf`; a.click();
    } catch { toast.error("Failed"); }
    finally { setDownloading(false); }
  };

  const toggleSelectAll = () => { selectedInvoices.length === filteredInvoices.length ? setSelectedInvoices([]) : setSelectedInvoices(filteredInvoices.map(i => i.id)); };
  const toggleSelectInvoice = (id) => { selectedInvoices.includes(id) ? setSelectedInvoices(selectedInvoices.filter(x => x !== id)) : setSelectedInvoices([...selectedInvoices, id]); };

  const handleSelectedPDF = async () => {
    if (!selectedInvoices.length) return;
    setDownloading(true);
    try { const r = await getAPSelectedInvoicesPDF(selectedInvoices); const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = `ap_selected_${selectedInvoices.length}.pdf`; a.click(); }
    catch { toast.error("Failed"); } finally { setDownloading(false); }
  };

  const handleSelectedZip = async () => {
    if (!selectedInvoices.length) return;
    setDownloading(true);
    try { const r = await getAPSelectedInvoicesZip(selectedInvoices); const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' })); const a = document.createElement('a'); a.href = url; a.download = `ap_selected_${selectedInvoices.length}.zip`; a.click(); }
    catch { toast.error("Failed"); } finally { setDownloading(false); }
  };

  const handleSelectedPrint = async () => {
    if (!selectedInvoices.length) return;
    setDownloading(true);
    try { const r = await getAPSelectedInvoicesPDF(selectedInvoices); const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })); const pw = window.open(url); if (pw) pw.onload = () => pw.print(); }
    catch { toast.error("Failed"); } finally { setDownloading(false); }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'generated': return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Generated</Badge>;
      case 'printed': return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Printed</Badge>;
      case 'closed': return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      case 'cancelled': return <Badge className="bg-red-100 text-red-700"><Ban className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const totalValue = filteredInvoices.reduce((sum, inv) => sum + (inv.grand_total || 0), 0);
  const pendingInvoices = filteredInvoices.filter(i => i.status === 'generated' || i.status === 'printed');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ap-invoice-history">
      <div className="flex items-center justify-between">
        <div />
        <Button size="sm" variant="outline" onClick={() => setShowSettings(!showSettings)} data-testid="ap-invoice-settings-btn">
          <Settings className="w-4 h-4 mr-1" />Invoice Settings
        </Button>
      </div>
      {showSettings && <APInvoiceSettings />}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><FileCheck className="w-8 h-8 text-blue-500" /><div><p className="text-2xl font-bold font-mono">{filteredInvoices.length}</p><p className="text-xs text-slate-500">Invoices</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Clock className="w-8 h-8 text-amber-500" /><div><p className="text-2xl font-bold font-mono">{pendingInvoices.length}</p><p className="text-xs text-slate-500">Pending Print</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><CheckCircle className="w-8 h-8 text-emerald-500" /><div><p className="text-2xl font-bold font-mono">{filteredInvoices.filter(i => i.status === 'printed' || i.status === 'closed').length}</p><p className="text-xs text-slate-500">Printed/Closed</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div><p className="text-2xl font-bold font-mono">Rs. {totalValue.toLocaleString()}</p><p className="text-xs text-slate-500">Total Value</p></div></CardContent></Card>
      </div>

      {/* Bulk Actions */}
      <Card className="bg-slate-900 text-white"><CardContent className="p-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div><h3 className="font-semibold text-lg">Bulk Actions</h3><p className="text-slate-400 text-sm">{selectedInvoices.length > 0 ? `${selectedInvoices.length} invoice(s) selected` : 'Download or print all invoices at once'}</p></div>
          <div className="flex items-center gap-3 flex-wrap">
            {selectedInvoices.length > 0 ? (<>
              <Button variant="secondary" onClick={handleSelectedPrint} disabled={downloading}><Printer className="w-4 h-4 mr-2" />Print Selected ({selectedInvoices.length})</Button>
              <Button variant="secondary" onClick={handleSelectedPDF} disabled={downloading}><FileText className="w-4 h-4 mr-2" />PDF ({selectedInvoices.length})</Button>
              <Button variant="outline" onClick={handleSelectedZip} disabled={downloading} className="text-white border-white hover:bg-white/10"><FileArchive className="w-4 h-4 mr-2" />ZIP ({selectedInvoices.length})</Button>
              <Button variant="ghost" onClick={() => setSelectedInvoices([])} className="text-slate-400 hover:text-white">Clear Selection</Button>
            </>) : (<>
              <Button variant="secondary" onClick={handleSelectedPrint} disabled={downloading || pendingInvoices.length === 0}><Printer className="w-4 h-4 mr-2" />Print All ({pendingInvoices.length})</Button>
              <Button variant="secondary" onClick={handleSelectedPDF} disabled={downloading || invoices.length === 0}><FileText className="w-4 h-4 mr-2" />Single PDF</Button>
              <Button variant="outline" onClick={handleSelectedZip} disabled={downloading || invoices.length === 0} className="text-white border-white hover:bg-white/10"><FileArchive className="w-4 h-4 mr-2" />ZIP</Button>
            </>)}
          </div>
        </div>
      </CardContent></Card>

      {/* Date Tabs */}
      {invoiceDates.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="ap-invoice-date-tabs">
          <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <Tabs value={dateFilter} onValueChange={setDateFilter}>
            <TabsList className="h-9">
              <TabsTrigger value="all" className="text-xs px-3">All ({invoices.length})</TabsTrigger>
              {invoiceDates.slice(0, 10).map(date => (
                <TabsTrigger key={date} value={date} className="text-xs px-3">
                  {date} ({invoices.filter(inv => { const d = new Date(inv.created_at || inv.invoice_date); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) === date; }).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Filters */}
      <Card><CardContent className="p-4"><div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Search invoice, PO, or store..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
        <Button variant="outline" onClick={() => setSortOrder(s => s === 'asc' ? 'desc' : 'asc')} className="flex items-center gap-2">{sortOrder === 'desc' ? <><ArrowDown className="w-4 h-4" />Newest First</> : <><ArrowUp className="w-4 h-4" />Oldest First</>}</Button>
        <Button variant="outline" onClick={() => { loadInvoices(); loadBatches(); }}><RefreshCw className="w-4 h-4" /></Button>
      </div></CardContent></Card>

      {/* Tabs for All Invoices and Batches */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all"><FileCheck className="w-4 h-4 mr-2" />All Invoices ({filteredInvoices.length})</TabsTrigger>
          <TabsTrigger value="batches"><Layers className="w-4 h-4 mr-2" />Bulk Batches ({batches.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card><CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium">Invoice History ({filteredInvoices.length})</CardTitle></CardHeader>
            <ScrollArea className="h-[450px]">
              {filteredInvoices.length === 0 ? (
                <div className="p-12 text-center text-slate-500"><FileCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p className="text-lg">No invoices found</p></div>
              ) : (
                <Table className="data-table">
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="w-10"><Checkbox checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0} onCheckedChange={toggleSelectAll} /></TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoice #</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">PO #</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Store</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => setSortOrder(s => s === 'asc' ? 'desc' : 'asc')}>
                      <div className="flex items-center gap-1">Date {sortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}</div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Items</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredInvoices.map(invoice => (
                      <TableRow key={invoice.id} className={selectedInvoices.includes(invoice.id) ? 'bg-blue-50' : ''}>
                        <TableCell><Checkbox checked={selectedInvoices.includes(invoice.id)} onCheckedChange={() => toggleSelectInvoice(invoice.id)} /></TableCell>
                        <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">{invoice.po_number}</TableCell>
                        <TableCell className="text-sm">{invoice.buyer_store || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{invoice.invoice_date}</TableCell>
                        <TableCell className="text-center font-mono">{invoice.total_pcs}</TableCell>
                        <TableCell className="text-right font-mono font-medium">Rs. {invoice.grand_total?.toLocaleString()}</TableCell>
                        <TableCell className="text-center">{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(invoice)}><Download className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handlePrint(invoice)}><Printer className="w-4 h-4" /></Button>
                            {invoice.status !== 'cancelled' && !invoice.voided && (
                              <Button variant="ghost" size="sm" onClick={() => setCancelTarget(invoice)} className="text-red-500 hover:text-red-700 hover:bg-red-50" title="Void & Restore Stock"><Ban className="w-4 h-4" /></Button>
                            )}
                            {invoice.voided && (
                              <span className="text-xs text-amber-600 font-medium px-2">VOIDED</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>

        <TabsContent value="batches">
          <Card><CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm font-medium">Invoice Batches - Print invoices generated together</CardTitle></CardHeader>
            <ScrollArea className="h-[450px]">
              {batches.length === 0 ? (
                <div className="p-12 text-center text-slate-500"><Layers className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p className="text-lg">No batches found</p><p className="text-sm mt-2">Use "Generate All" in PO Processing to create invoice batches</p></div>
              ) : (
                <Table className="data-table">
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Batch ID</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoices</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoice Numbers</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Total Value</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {batches.map(batch => (
                      <TableRow key={batch._id}>
                        <TableCell className="font-mono text-sm">{batch._id?.slice(0, 8)}...</TableCell>
                        <TableCell><Badge variant="secondary">{batch.count} invoices</Badge></TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">{batch.invoices?.slice(0, 3).join(', ')}{batch.invoices?.length > 3 ? '...' : ''}</TableCell>
                        <TableCell className="text-right font-mono font-medium">Rs. {batch.total_value?.toLocaleString()}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleBatchDownload(batch._id)} disabled={downloading}><Download className="w-4 h-4 mr-1" />PDF</Button>
                            <Button size="sm" onClick={() => handleBatchPrint(batch._id)} disabled={downloading}><Printer className="w-4 h-4 mr-1" />Print All</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent><DialogHeader><DialogTitle>Cancel Invoice</DialogTitle>
          <DialogDescription>Are you sure you want to cancel invoice <strong>{cancelTarget?.invoice_number}</strong>? This will restore {cancelTarget?.total_pcs || 0} items back to inventory.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>No, Keep It</Button>
          <Button variant="destructive" onClick={handleCancelInvoice} disabled={cancelling}>{cancelling ? "Cancelling..." : "Yes, Cancel Invoice"}</Button>
        </div></DialogContent>
      </Dialog>
    </div>
  );
}

function APInvoiceSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getAPSettings().then(r => setSettings(r.data)).catch(() => {}); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await updateAPSettings(settings); toast.success("AP invoice settings saved"); } catch { toast.error("Failed"); }
    setSaving(false);
  };

  if (!settings) return null;

  return (
    <Card data-testid="ap-invoice-settings">
      <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">AP Invoice Settings</CardTitle></CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><label className="text-xs font-medium text-slate-600">Invoice Prefix</label><Input value={settings.invoice_prefix || ""} onChange={e => setSettings({...settings, invoice_prefix: e.target.value})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">Financial Year</label><Input value={settings.financial_year || ""} onChange={e => setSettings({...settings, financial_year: e.target.value})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">GST Rate (%)</label><Input type="number" value={settings.gst_rate || 18} onChange={e => setSettings({...settings, gst_rate: parseFloat(e.target.value)})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">Last Invoice #</label><Input type="number" value={settings.last_invoice_number || 100} onChange={e => setSettings({...settings, last_invoice_number: parseInt(e.target.value)})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">Seller Name</label><Input value={settings.seller_name || ""} onChange={e => setSettings({...settings, seller_name: e.target.value})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">Seller GST</label><Input value={settings.seller_gst || ""} onChange={e => setSettings({...settings, seller_gst: e.target.value})} className="mt-1" /></div>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="mt-3"><Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save Settings"}</Button>
      </CardContent>
    </Card>
  );
}
